/**
 * State Action Dialog Component
 * 
 * Dialog for configuring state actions with flow direction and change type selection.
 * Supports Both, Forward, and Backward flow directions with validation.
 */

import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Select,
  AutoComplete,
  Radio,
  Typography,
  Divider,
  message
} from 'antd';
import {
  StateAction,
  StateActionNew,
  ChangeTypeDefinition,
  FlowDirection,
  getChangeTypeName,
  isNewChangeTypeStructure,
  normalizeStateAction
} from '../../types/globalProperties';

const { Option } = Select;
const { Text } = Typography;

// State action options (4-type model + none)
const ACTION_OPTIONS: Array<{ value: StateActionNew | 'none'; label: string }> = [
  { value: 'System', label: 'System' },
  { value: 'User', label: 'User' },
  { value: 'Calculate', label: 'Calculate' },
  { value: 'Reference', label: 'Reference' },
  { value: 'none', label: 'None (Remove State Action)' },
];

// Flow direction options
const FLOW_DIRECTION_OPTIONS: Array<{ value: FlowDirection; label: string }> = [
  { value: 'both', label: 'Both' },
  { value: 'forward', label: 'Forward' },
  { value: 'backward', label: 'Backward' },
];

export interface StateActionConfig {
  state: string;
  flowType: 'operational' | 'improvement';
  flowDirection: FlowDirection;
  forward?: {
    state_action: StateAction;
    change_type_ref: string;
  } | null; // null indicates removal
  backward?: {
    state_action: StateAction;
    change_type_ref: string;
  } | null; // null indicates removal
  both?: {
    state_action: StateAction;
    change_type_ref: string;
  } | null; // null indicates removal
}

interface StateActionDialogProps {
  visible: boolean;
  state: string;
  flowType: 'operational' | 'improvement';
  currentStateAction?: StateAction;
  currentFlowDirection?: FlowDirection;
  currentChangeTypeRef?: string;
  forwardAction?: StateAction;
  forwardChangeTypeRef?: string;
  backwardAction?: StateAction;
  backwardChangeTypeRef?: string;
  changeTypeDefinitions: ChangeTypeDefinition[];
  allStateActions?: Array<{ state: string; flowType: 'operational' | 'improvement'; stateActionType: StateAction }>; // For checking improvement dependencies
  onSave: (config: StateActionConfig) => void;
  onCancel: () => void;
}

export const StateActionDialog: React.FC<StateActionDialogProps> = ({
  visible,
  state,
  flowType,
  currentStateAction,
  currentFlowDirection = 'both',
  currentChangeTypeRef,
  forwardAction,
  forwardChangeTypeRef,
  backwardAction,
  backwardChangeTypeRef,
  changeTypeDefinitions,
  allStateActions = [],
  onSave,
  onCancel
}) => {
  const [form] = Form.useForm();
  const [flowDirection, setFlowDirection] = useState<FlowDirection>(currentFlowDirection);
  const bothStateAction = Form.useWatch('both_state_action', form);
  const forwardStateAction = Form.useWatch('forward_state_action', form);
  const backwardStateAction = Form.useWatch('backward_state_action', form);

  useEffect(() => {
    if (visible) {
      // Reset form when dialog opens
      form.resetFields();
      setFlowDirection(currentFlowDirection || 'both');
      
      // Pre-populate form if editing
      if (currentFlowDirection === 'both') {
        if (currentStateAction) {
          const normalizedAction = normalizeStateAction(currentStateAction);
          if (normalizedAction !== 'none') {
            form.setFieldsValue({
              flow_direction: 'both',
              both_state_action: normalizedAction,
              both_change_type_ref: currentChangeTypeRef || '',
            });
          }
        } else {
          form.setFieldsValue({
            flow_direction: 'both',
          });
        }
      } else if (currentFlowDirection === 'forward') {
        const forwardNormalized = forwardAction ? normalizeStateAction(forwardAction) : undefined;
        const backwardNormalized = backwardAction ? normalizeStateAction(backwardAction) : undefined;
        
        form.setFieldsValue({
          flow_direction: 'forward',
          forward_state_action: forwardNormalized && forwardNormalized !== 'none' ? forwardNormalized : undefined,
          forward_change_type_ref: forwardChangeTypeRef || '',
          backward_state_action: backwardNormalized && backwardNormalized !== 'none' ? backwardNormalized : undefined,
          backward_change_type_ref: backwardChangeTypeRef || '',
        });
      } else if (currentFlowDirection === 'backward') {
        const forwardNormalized = forwardAction ? normalizeStateAction(forwardAction) : undefined;
        const backwardNormalized = backwardAction ? normalizeStateAction(backwardAction) : undefined;
        
        form.setFieldsValue({
          flow_direction: 'backward',
          backward_state_action: backwardNormalized && backwardNormalized !== 'none' ? backwardNormalized : undefined,
          backward_change_type_ref: backwardChangeTypeRef || '',
          forward_state_action: forwardNormalized && forwardNormalized !== 'none' ? forwardNormalized : undefined,
          forward_change_type_ref: forwardChangeTypeRef || '',
        });
      } else {
        form.setFieldsValue({
          flow_direction: 'both',
        });
      }
    }
  }, [visible, currentStateAction, currentFlowDirection, currentChangeTypeRef, forwardAction, forwardChangeTypeRef, backwardAction, backwardChangeTypeRef, form]);

  const handleFlowDirectionChange = (direction: FlowDirection) => {
    setFlowDirection(direction);
    form.setFieldsValue({ flow_direction: direction });
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const config: StateActionConfig = {
        state,
        flowType,
        flowDirection: values.flow_direction,
      };

      if (values.flow_direction === 'both') {
        if (!values.both_state_action) {
          void message.error('State action is required for "Both" direction');
          return;
        }
        const normalizedAction = normalizeStateAction(values.both_state_action);
        // Allow 'none' to remove the state action
        if (normalizedAction === 'none') {
          config.both = null; // Signal removal
        } else {
          config.both = {
            state_action: normalizedAction,
            change_type_ref: values.both_change_type_ref || '',
          };
          
          // Warn if operational Reference without corresponding improvement action
          if (flowType === 'operational' && normalizedAction === 'Reference') {
            const hasImprovementAction = allStateActions.some(
              sa => sa.flowType === 'improvement' && sa.state === state && sa.stateActionType !== 'none'
            );
            if (!hasImprovementAction) {
              void message.warning(
                `Operational state "${state}" has a Reference action, but no corresponding improvement state action exists. ` +
                `An improvement state action is typically required to provide the value that the operational Reference will use.`,
                6
              );
            }
          }
        }
      } else if (values.flow_direction === 'forward') {
        if (!values.forward_state_action) {
          void message.error('State action is required for "Forward" direction');
          return;
        }
        const normalizedAction = normalizeStateAction(values.forward_state_action);
        // Allow 'none' to remove the state action
        if (normalizedAction === 'none') {
          config.forward = null; // Signal removal
        } else {
          config.forward = {
            state_action: normalizedAction,
            change_type_ref: values.forward_change_type_ref || '',
          };
          
          // Warn if operational Reference without corresponding improvement action
          if (flowType === 'operational' && normalizedAction === 'Reference') {
            const hasImprovementAction = allStateActions.some(
              sa => sa.flowType === 'improvement' && sa.state === state && sa.stateActionType !== 'none'
            );
            if (!hasImprovementAction) {
              void message.warning(
                `Operational state "${state}" has a Reference action, but no corresponding improvement state action exists. ` +
                `An improvement state action is typically required to provide the value that the operational Reference will use.`,
                6
              );
            }
          }
        }
        
        // Optional backward
        if (values.backward_state_action && values.backward_state_action !== 'none') {
          const backwardNormalized = normalizeStateAction(values.backward_state_action);
          if (backwardNormalized !== 'none') {
            config.backward = {
              state_action: backwardNormalized,
              change_type_ref: values.backward_change_type_ref || '',
            };
          }
        }
      } else if (values.flow_direction === 'backward') {
        if (!values.backward_state_action) {
          void message.error('State action is required for "Backward" direction');
          return;
        }
        const normalizedAction = normalizeStateAction(values.backward_state_action);
        // Allow 'none' to remove the state action
        if (normalizedAction === 'none') {
          config.backward = null; // Signal removal
        } else {
          config.backward = {
            state_action: normalizedAction,
            change_type_ref: values.backward_change_type_ref || '',
          };
          
          // Warn if operational Reference without corresponding improvement action
          if (flowType === 'operational' && normalizedAction === 'Reference') {
            const hasImprovementAction = allStateActions.some(
              sa => sa.flowType === 'improvement' && sa.state === state && sa.stateActionType !== 'none'
            );
            if (!hasImprovementAction) {
              void message.warning(
                `Operational state "${state}" has a Reference action, but no corresponding improvement state action exists. ` +
                `An improvement state action is typically required to provide the value that the operational Reference will use.`,
                6
              );
            }
          }
        }
        
        // Optional forward
        if (values.forward_state_action && values.forward_state_action !== 'none') {
          const forwardNormalized = normalizeStateAction(values.forward_state_action);
          if (forwardNormalized !== 'none') {
            config.forward = {
              state_action: forwardNormalized,
              change_type_ref: values.forward_change_type_ref || '',
            };
          }
        }
      }

      onSave(config);
    } catch (error) {
      console.error('Validation error:', error);
    }
  };

  // Get change types that match a state action type
  const _getChangeTypesForAction = (actionType: StateAction | undefined): ChangeTypeDefinition[] => {
    if (!actionType) return changeTypeDefinitions;
    
    const normalized = normalizeStateAction(actionType);
    if (normalized === 'none') return changeTypeDefinitions;
    
    // Filter change types that match the state action type
    // For new structure, check if change_type_name matches or starts with the action type
    return changeTypeDefinitions.filter(ct => {
      if (isNewChangeTypeStructure(ct)) {
        const name = getChangeTypeName(ct);
        // Match if name starts with the action type (e.g., "User", "User2", "User3")
        return name.startsWith(normalized);
      }
      return true; // Include all for legacy structure
    });
  };

  // Get all change type names for dropdown
  const getAllChangeTypeNames = (): string[] => {
    return changeTypeDefinitions.map(ct => getChangeTypeName(ct)).filter(Boolean);
  };

  return (
    <Modal
      title={`Configure State Action: ${state} (${flowType})`}
      open={visible}
      onOk={handleSave}
      onCancel={onCancel}
      width={700}
      okText="Save"
      cancelText="Cancel"
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          flow_direction: 'both',
        }}
      >
        <Form.Item
          name="flow_direction"
          label="Flow Direction"
          rules={[{ required: true, message: 'Flow direction is required' }]}
        >
          <Radio.Group
            onChange={(e) => handleFlowDirectionChange(e.target.value)}
            value={flowDirection}
          >
            {FLOW_DIRECTION_OPTIONS.map(option => (
              <Radio key={option.value} value={option.value}>
                {option.label}
              </Radio>
            ))}
          </Radio.Group>
        </Form.Item>

        <Divider />

        {/* Both Direction */}
        {flowDirection === 'both' && (
          <>
            <Form.Item
              name="both_state_action"
              label="State Action"
              rules={[{ required: true, message: 'State action is required' }]}
            >
              <Select placeholder="Select state action">
                {ACTION_OPTIONS.map(option => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            {bothStateAction !== 'none' && (
              <Form.Item
                name="both_change_type_ref"
                label="Change Type"
                tooltip="Select an existing change type or type a new name to create one"
              >
                <AutoComplete
                  placeholder="Select or type change type name (leave empty to auto-create)"
                  allowClear
                  options={getAllChangeTypeNames().map(name => ({
                    value: name,
                    label: name
                  }))}
                  filterOption={(inputValue, option) => {
                    if (!inputValue) return true;
                    return String(option?.value ?? '').toLowerCase().includes(inputValue.toLowerCase());
                  }}
                />
              </Form.Item>
            )}
          </>
        )}

        {/* Forward Direction */}
        {flowDirection === 'forward' && (
          <>
            <Text strong>Forward Direction</Text>
            <Form.Item
              name="forward_state_action"
              label="State Action"
              rules={[{ required: true, message: 'Forward state action is required' }]}
            >
              <Select placeholder="Select forward state action">
                {ACTION_OPTIONS.map(option => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              name="forward_change_type_ref"
              label="Change Type"
              tooltip="Select an existing change type or type a new name to create one"
            >
              <AutoComplete
                placeholder="Select or type change type name (leave empty to auto-create)"
                allowClear
                options={getAllChangeTypeNames().map(name => ({
                  value: name,
                  label: name
                }))}
                filterOption={(inputValue, option) => {
                  if (!inputValue) return true;
                  return String(option?.value ?? '').toLowerCase().includes(inputValue.toLowerCase());
                }}
              />
            </Form.Item>

            <Divider />

            <Text strong>Backward Direction (Optional)</Text>
            <Form.Item
              name="backward_state_action"
              label="State Action"
            >
              <Select placeholder="Select backward state action (optional)">
                {ACTION_OPTIONS.map(option => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            {backwardStateAction && backwardStateAction !== 'none' && (
              <Form.Item
                name="backward_change_type_ref"
                label="Change Type"
                tooltip="Select an existing change type or type a new name to create one"
              >
                <AutoComplete
                  placeholder="Select or type change type name (leave empty to auto-create)"
                  allowClear
                  options={getAllChangeTypeNames().map(name => ({
                    value: name,
                    label: name
                  }))}
                  filterOption={(inputValue, option) => {
                    if (!inputValue) return true;
                    return String(option?.value ?? '').toLowerCase().includes(inputValue.toLowerCase());
                  }}
                />
              </Form.Item>
            )}
          </>
        )}

        {/* Backward Direction */}
        {flowDirection === 'backward' && (
          <>
            <Text strong>Backward Direction</Text>
            <Form.Item
              name="backward_state_action"
              label="State Action"
              rules={[{ required: true, message: 'Backward state action is required' }]}
            >
              <Select placeholder="Select backward state action">
                {ACTION_OPTIONS.map(option => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            {backwardStateAction !== 'none' && (
              <Form.Item
                name="backward_change_type_ref"
                label="Change Type"
                tooltip="Select an existing change type or type a new name to create one"
              >
                <AutoComplete
                  placeholder="Select or type change type name (leave empty to auto-create)"
                  allowClear
                  options={getAllChangeTypeNames().map(name => ({
                    value: name,
                    label: name
                  }))}
                  filterOption={(inputValue, option) => {
                    if (!inputValue) return true;
                    return String(option?.value ?? '').toLowerCase().includes(inputValue.toLowerCase());
                  }}
                />
              </Form.Item>
            )}

            <Divider />

            <Text strong>Forward Direction (Optional)</Text>
            <Form.Item
              name="forward_state_action"
              label="State Action"
            >
              <Select placeholder="Select forward state action (optional)">
                {ACTION_OPTIONS.map(option => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            {forwardStateAction && forwardStateAction !== 'none' && (
              <Form.Item
                name="forward_change_type_ref"
                label="Change Type"
                tooltip="Select an existing change type or type a new name to create one"
              >
                <AutoComplete
                  placeholder="Select or type change type name (leave empty to auto-create)"
                  allowClear
                  options={getAllChangeTypeNames().map(name => ({
                    value: name,
                    label: name
                  }))}
                  filterOption={(inputValue, option) => {
                    if (!inputValue) return true;
                    return String(option?.value ?? '').toLowerCase().includes(inputValue.toLowerCase());
                  }}
                />
              </Form.Item>
            )}
          </>
        )}
      </Form>
    </Modal>
  );
};

